/**
 * Browser fingerprinting via FingerprintJS (open source).
 *
 * Role note: the fingerprint is an ANTI-ABUSE SIGNAL, not the primary
 * identity. The OSS visitorId is a hash of device characteristics, so
 * identical popular devices collide by design - see lib/client-id.ts
 * for the per-install identity that quotas actually key on. The server
 * uses this value only for windowed abuse backstops (many fresh client
 * IDs on one fingerprint = someone farming resets).
 */
import { getClientId } from "./client-id";

// Cache the PROMISE, not the value: concurrent first callers (the
// initial pageview + any API call racing it) must share one engine
// run and land on the same id - caching the resolved value let each
// racer mint its own fallback id when the engine failed.
let inflight: Promise<string> | null = null;
let resolved: string | null = null;

export function getFingerprint(): Promise<string> {
  if (!inflight) {
    inflight = (async () => {
      try {
        // Loaded on first use: imported at the top, the library (~35 KB)
        // was parsed on every page during load, though the engine itself
        // waits for an interaction or 8 s (Google's runs, 2026-10-27).
        const { default: FingerprintJS } = await import("@fingerprintjs/fingerprintjs");
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        return result.visitorId;
      } catch {
        // Stable fallback: derive from the persistent client ID so a
        // browser where FingerprintJS always fails still presents ONE
        // identity across reloads (a per-load random id made the anon
        // rate limit resettable by refreshing).
        return "fpfail-" + (getClientId() || "unknown");
      }
    })();
    void inflight.then((v) => { resolved = v; });
  }
  return inflight;
}

/** The fingerprint if the engine has already run, else null. Never
 *  starts it. */
export function peekFingerprint(): string | null {
  return resolved;
}

let warmScheduled = false;

/**
 * Run the engine later: on the first interaction, or 8 s after the call,
 * whichever comes first. For the pageview tracker, which only wants the
 * fingerprint when it is free: the engine (canvas, fonts, WebGL probes)
 * took 250-700 ms of a phone's main thread during page load on every
 * page (Lighthouse, 2026-10-07). API calls that need it still await
 * getFingerprint() directly.
 */
export function warmFingerprintLater(): void {
  if (warmScheduled || inflight || typeof window === "undefined") return;
  warmScheduled = true;
  const events = ["pointerdown", "keydown", "scroll", "touchstart"] as const;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const go = () => {
    events.forEach((ev) => window.removeEventListener(ev, go));
    if (timer) clearTimeout(timer);
    void getFingerprint();
  };
  events.forEach((ev) => window.addEventListener(ev, go, { passive: true, once: true }));
  timer = setTimeout(go, 8000);
}
